import { useCallback, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import LoadingButton from '@atlaskit/button/loading-button';
import Heading from '@atlaskit/heading';
import { Box, Grid, Text, xcss, media } from '@atlaskit/primitives';

import { useScreenManager } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-screen-manager.tsx';
import { getCurrentUrlWithParams } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/utils';

import { messages } from './messages';
import { Redirect, WorkOption } from './types';
import { aiLedOnboardingWorkOptions } from './utils';

type QueryParams = {
	redirect: Redirect;
} | null;

type Props = {
	selectedOption: string | null;
	onSelectOption: (optionId: string) => void;
};

const LeftHandSideV2 = ({ selectedOption, onSelectOption }: Props) => {
	const { formatMessage } = useIntl();
	const { nextScreen } = useScreenManager();
	const [queryParams, setQueryParams] = useState<QueryParams>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleOptionClick = (option: WorkOption) => {
		onSelectOption(option.id);
		setQueryParams({ redirect: option.redirect });
	};

	const handleKeyDown = (e: React.KeyboardEvent, option: WorkOption) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleOptionClick(option);
		}
	};

	const handleOnClick = useCallback(() => {
		if (queryParams) {
			const updatedUrl = getCurrentUrlWithParams({
				aiLedOnboarding: queryParams.redirect,
			});
			history.replaceState({}, '', updatedUrl);

			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'workOnFirstNextButtonV2',
				source: '/ondemand/signup/confirmation',
				attributes: queryParams.redirect
					? {
							aiLedOnboardingFlow: queryParams.redirect === 'jira' ? 'jira' : 'confluence',
						}
					: undefined,
			}).fire();

			nextScreen();
		}
	}, [queryParams, nextScreen, createAnalyticsEvent]);

	return (
		<Box xcss={wrapper}>
			<Box xcss={content}>
				<Box xcss={heading}>
					<Heading size="large" as="h1" testId="work-on-first-header-v2">
						<FormattedMessage {...messages.header} />
					</Heading>
					<Text color="color.text.subtle" testId="work-on-first-subheader-v2">
						<FormattedMessage {...messages.subHeaderV2} />
					</Text>
				</Box>
				<Grid gap="space.200" xcss={responsiveGrid} role="list">
					{aiLedOnboardingWorkOptions.map((option) => (
						<Box
							xcss={[responsiveCard, selectedOption === option.id && cardSelected]}
							key={option.id}
							onClick={() => handleOptionClick(option)}
							onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, option)}
							role="listitem"
							tabIndex={0}
							testId={`work-option-v2-${option.id}`}
						>
							<option.icon spacing="spacious" color="var(--ds-icon-subtle)" label={option.id} />
							<Text color="color.text" weight="bold">
								{formatMessage(option.name)}
							</Text>
						</Box>
					))}
				</Grid>
			</Box>
			<Box xcss={button}>
				<LoadingButton
					appearance="primary"
					isDisabled={selectedOption === null}
					onClick={handleOnClick}
					testId="work-on-first-primary-cta-v2"
				>
					<FormattedMessage {...messages.nextButton} />
				</LoadingButton>
			</Box>
		</Box>
	);
};

const wrapper = xcss({
	display: 'flex',
	flexDirection: 'column',
	padding: 'space.800',
	alignItems: 'stretch',
	gap: 'space.300',
	justifyContent: 'space-between',
});

const content = xcss({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: 'space.400',
});

const heading = xcss({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: 'space.150',
	alignSelf: 'stretch',
});

const responsiveGrid = xcss({
	width: '100%',
	gridTemplateColumns: '1fr',
	height: 'auto',
	[media.above.sm]: {
		gridTemplateColumns: '1fr',
		height: '236px',
	},
});

const responsiveCard = xcss({
	display: 'flex',
	minHeight: '68px',
	alignItems: 'center',
	paddingInlineStart: 'space.150',
	paddingInlineEnd: 'space.150',
	gap: 'space.100',
	borderRadius: 'border.radius.200',
	border: '1px solid',
	borderColor: 'color.border',
	background: 'color.background.input',
	cursor: 'pointer',
	[media.above.sm]: {
		minHeight: '32px',
	},
	':hover': {
		borderColor: 'color.border.selected',
	},
});

const cardSelected = xcss({
	border: '2px solid',
	borderColor: 'color.border.selected',
});

const button = xcss({
	display: 'flex',
	justifyContent: 'end',
});

export default LeftHandSideV2;
